import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode'; // { jwtDecode } is correct, without brackets it gives error
import axiosInstance from '../config/axiosInstance'; 

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const logoutTimer = useRef(null); 

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('token');
    setIsLoading(false);
  }, []);

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
    }
  }, []);

  const setLogoutTimer = useCallback(
    (timeUntilExpiry) => {
      clearLogoutTimer(); // Cleans any existing timer
      logoutTimer.current = setTimeout(() => {
        logout(); // Logs out automatically when time expires
      }, timeUntilExpiry);
    },
    [logout, clearLogoutTimer]
  );

  const fetchUserData = async (userId) => {
    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw new Error('Failed to fetch user data');
    }
  };

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch (err) {
      console.error('Error decoding token:', err);
      return true; 
    }
  };

  const hasFeature = useCallback((featureKey) => {
    return user?.clientID?.features?.includes(featureKey);
  }, [user]);

  // Re-fetches the logged-in user (incl. the client features), so changes like
  // newly granted features become visible without a full page reload.
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) return;

    try {
      const decoded = jwtDecode(token);
      const userData = await fetchUserData(decoded.userId);
      setUser(userData);
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  }, []);

  // Refresh the user data whenever the tab regains focus, so changes made
  // elsewhere (other tab, other user) are picked up without a page reload.
  useEffect(() => {
    const handleFocus = () => {
      refreshUser();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshUser]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired(token)) {
      const decoded = jwtDecode(token);
      setLogoutTimer(decoded.exp * 1000 - Date.now());
      fetchUserData(decoded.userId)
        .then(userData => {
          setUser(userData);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error fetching user data on startup:', err);
          logout();
        });
    } else {
      logout();
    }
  }, [logout, setLogoutTimer, clearLogoutTimer]);

  const login = async (token) => {
    try {
      if (isTokenExpired(token)) {
        throw new Error('Token expired');
      }
      localStorage.setItem('token', token);
      const decoded = jwtDecode(token);
      setLogoutTimer(decoded.exp * 1000 - Date.now()); // Set the logout timer

      const userData = await fetchUserData(decoded.userId);
      setUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      logout();
    }
  };

  return (
      <AuthContext.Provider value={{ user, setUser, login, logout, isLoading, isTokenExpired, hasFeature, refreshUser }}>
        {children}
      </AuthContext.Provider>
  );
};
